import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { LayoutDashboard, Package, ScanLine, CalendarDays, LogOut, Menu, X, Users, Shield, Wifi, WifiOff, RefreshCw, CreditCard, Crown, Zap, Sparkles, BarChart3, ShieldCheck } from "lucide-react";
import { useOnline } from "../lib/online";
import { useBilling } from "../lib/billing";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/products", label: "Products", icon: Package, testid: "nav-products" },
  { to: "/scanner", label: "Scanner", icon: ScanLine, testid: "nav-scanner" },
  { to: "/events", label: "Events", icon: CalendarDays, testid: "nav-events" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
  { to: "/team", label: "Team", icon: Users, testid: "nav-team" },
  { to: "/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
];

const SUPER_NAV = { to: "/admin", label: "Admin", icon: ShieldCheck, testid: "nav-admin" };

const TIER_META = {
  free: { label: "Free", icon: Sparkles, bg: "!bg-[#F4F2EC]", fg: "" },
  pro: { label: "Pro", icon: Zap, bg: "!bg-[#C2410C] !text-white", fg: "" },
  business: { label: "Business", icon: Crown, bg: "!bg-[#111] !text-white", fg: "" },
};

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const { online, syncing, syncNow, lastSync, queued } = useOnline();
  const { billing } = useBilling();
  const tier = billing?.tier || "free";
  const tierMeta = TIER_META[tier] || TIER_META.free;
  const TierIcon = tierMeta.icon;
  const trialing = billing?.status === "trialing";
  const visibleNavItems = user?.is_super_admin ? [...navItems, SUPER_NAV] : navItems;

  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top Bar */}
      <header className="bg-white border-b-2 border-black sticky top-0 z-40">
        <div className="accent-strip" />
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 h-14 sm:h-16 flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              className="lg:hidden p-2 border-2 border-black"
              onClick={() => setOpen(!open)}
              data-testid="nav-toggle"
              aria-label="Toggle navigation"
            >
              {open ? <X size={18}/> : <Menu size={18}/>}
            </button>
            <div className="w-9 h-9 bg-[#C2410C] border-2 border-black flex items-center justify-center">
              <Package className="text-white" size={18}/>
            </div>
            <div className="font-heading font-black text-base lg:text-lg uppercase tracking-tight whitespace-nowrap hidden 2xl:block">Vendor Vault</div>
          </div>

          {/*
            Desktop nav:
            - lg / xl (1024–1535): icon-only chips so the topbar fits even with 8 items + long usernames
            - 2xl (1536+): icon + label
          */}
          <nav className="hidden lg:flex items-center gap-1 mx-auto">
            {visibleNavItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={it.testid}
                title={it.label}
                className={({ isActive }) =>
                  `px-2 2xl:px-3 py-2 font-mono text-xs uppercase tracking-widest border-2 border-transparent inline-flex items-center gap-2 ${
                    isActive ? "bg-[#111] text-white border-black" : "hover:bg-[#F4F2EC] hover:border-black"
                  }`
                }
              >
                <it.icon size={14}/>
                <span className="hidden 2xl:inline">{it.label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button
              type="button"
              onClick={syncNow}
              title={online ? (lastSync ? `Last sync: ${new Date(lastSync).toLocaleString()}` : "Online") : "Offline — using cached data"}
              className={`brutal-badge !py-1 !px-2 cursor-pointer flex-shrink-0 ${online ? (queued > 0 ? "!bg-[#FFB100]" : "!bg-[#4A7C59] !text-white") : "!bg-[#FFB100]"}`}
              data-testid="online-status-pill"
            >
              {online ? <Wifi size={11}/> : <WifiOff size={11}/>}
              <span className="ml-1 hidden sm:inline">
                {online ? (queued > 0 ? `Online · ${queued} queued` : "Online") : (queued > 0 ? `Offline · ${queued} queued` : "Offline")}
              </span>
              {queued > 0 && <span className="sm:hidden ml-1 font-bold">{queued}</span>}
              {syncing && <RefreshCw size={10} className="ml-1 animate-spin"/>}
            </button>
            {/* Tier badge — desktop only; mobile sees it in the menu */}
            <button
              onClick={() => navigate("/billing")}
              className={`brutal-badge !py-1 !px-2 cursor-pointer ${tierMeta.bg} hidden md:inline-flex flex-shrink-0`}
              data-testid="tier-badge"
              title={trialing ? "Pro trial — click to manage" : `${tierMeta.label} plan — click to manage`}
            >
              <TierIcon size={11}/>
              <span className="ml-1">{trialing ? "Trial" : tierMeta.label}</span>
            </button>
            {user?.picture && (
              <img src={user.picture} alt="" className="w-7 h-7 sm:w-8 sm:h-8 border-2 border-black object-cover flex-shrink-0"/>
            )}
            <div className="hidden 2xl:flex flex-col items-start leading-tight min-w-0">
              <span className="font-mono text-xs uppercase tracking-widest truncate max-w-[120px]">{user?.name || user?.email}</span>
              {user?.role && (
                <span className={`font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-1 ${user.role === "admin" ? "text-[#C2410C]" : "text-[#4A4A4A]"}`}>
                  <Shield size={10}/> {user.role}
                </span>
              )}
            </div>
            {/* Mobile/tablet: icon-only logout. Desktop (lg): full text. */}
            <button
              className="brutal-btn brutal-btn-secondary !py-2 !px-2 sm:!px-3 text-xs flex-shrink-0"
              onClick={doLogout}
              data-testid="logout-btn"
              aria-label="Logout"
              title="Logout"
            >
              <LogOut size={14}/>
              <span className="hidden lg:inline ml-1">Logout</span>
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t-2 border-black bg-white">
            <div className="max-w-[1600px] mx-auto px-4 py-3 flex flex-col gap-2">
              {/* User + tier strip on mobile */}
              <div className="flex items-center justify-between gap-2 pb-2 border-b border-black/20">
                <div className="font-mono text-xs uppercase tracking-widest truncate">{user?.name || user?.email}</div>
                <button
                  onClick={() => { setOpen(false); navigate("/billing"); }}
                  className={`brutal-badge !py-1 !px-2 cursor-pointer ${tierMeta.bg} flex-shrink-0`}
                  data-testid="tier-badge-mobile"
                >
                  <TierIcon size={11}/>
                  <span className="ml-1">{trialing ? "Trial" : tierMeta.label}</span>
                </button>
              </div>
              {visibleNavItems.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  onClick={() => setOpen(false)}
                  data-testid={`${it.testid}-mobile`}
                  className={({ isActive }) =>
                    `px-3 py-2 font-mono text-xs uppercase tracking-widest border-2 ${
                      isActive ? "bg-[#111] text-white border-black" : "bg-white border-black"
                    }`
                  }
                >
                  <span className="inline-flex items-center gap-2">
                    <it.icon size={14}/> {it.label}
                  </span>
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 max-w-[1600px] w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">{children}</main>

      <footer className="border-t-2 border-black bg-white mt-8">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-8 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A]">
            Vendor Vault · Built for Vendors
          </div>
          <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A]">
            <NavLink to="/help" className="hover:text-black underline-offset-2 hover:underline" data-testid="footer-help">Help</NavLink>
            <NavLink to="/terms" className="hover:text-black underline-offset-2 hover:underline" data-testid="footer-terms">Terms</NavLink>
            <NavLink to="/privacy" className="hover:text-black underline-offset-2 hover:underline" data-testid="footer-privacy">Privacy</NavLink>
            <span>© {new Date().getFullYear()}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
